import * as THREE from "three/webgpu";
import {SketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {CollectSource, ISketchCollect} from './ISketchObject';
import {isSketchLineObject} from '../guards/isSketchLineObject';
import {isSketchSplineObject} from '../guards/isSketchSplineObject';
import {isSketchStyleSplineObject} from '../guards/isSketchStyleSplineObject';
import {isSketchParabolaObject} from '../guards/isSketchParabolaObject';
import {isSketchEllipseObject} from '../guards/isSketchEllipseObject';
import {isAbstractSketchEdgeObject} from '../guards/isAbstractSketchEdgeObject';
import type {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import type {SketchPointObject} from './SketchPointObject';
import {SketchContour,} from '../meshProxy/SketchContour';
import {SketchSnapshot} from '../meshProxy/SketchSnapshot';
import {createSketchCloneContext, type SketchCloneContext} from './SketchCloneContext';
import {createSketchEntityPersistentId} from '../../sketch/identity/SketchPersistentIdFactory';
import {isSketchArcObject} from '../guards/isSketchArcObject';
import {isSketchPointObject} from '../guards/isSketchPointObject';
import {disposeSketchContour} from './clearSketchContourDisplays';
import {SketchClosureResolver, type SketchClosureTraversalStep,} from '../../sketch/closure';

/**
 * {@link SketchCollectionObject} 构造参数。
 */
export interface SketchCollectionObjectOptions {
    /** 构成封闭图形的源对象列表。 */
    sources: CollectSource[];
    /** 集合对象所属参考面。 */
    referencePlane: ReferencePlane;
    /** 集合创建来源。 */
    creationSource?: SketchCollectionCreationSource;
}

/**
 * 草图集合创建来源枚举。
 */
export enum SketchCollectionCreationSource {
    /** 通用来源。 */
    Generic = 'generic',
    /** 矩形绘制创建。 */
    Rectangle = 'rectangle',
    /** 多边形绘制创建。 */
    Polygon = 'polygon',
}

/**
 * 判断对象是否为草图线段几何对象。
 * @param value 待判断的 Three 对象。
 * @returns 当对象满足线段几何协议时返回 true。
 */

/**
 * 判断对象是否为可绑定闭合集合的集合源对象。
 * 说明：
 * 1. 当前允许直线、圆弧、部分椭圆、抛物线与两类样条边对象进入集合；
 * 2. 通过现有 `isXXX` 类型守卫统一收窄，禁止使用动态属性探测；
 * 3. 判定失败直接返回 false，由调用方决定是否抛错。
 * @param value 待判断对象。
 * @returns 当对象为合法集合源时返回 true。
 */
function isSketchCollectionSource(value: THREE.Object3D): value is CollectSource {
    return isSketchLineObject(value)
        || isSketchArcObject(value)
        || (isSketchEllipseObject(value) && value.isPartialEllipse())
        || isSketchParabolaObject(value)
        || isSketchSplineObject(value)
        || isSketchStyleSplineObject(value);
}

/**
 * 集合直接子层级中可复制的草图对象。
 */
type CloneableDirectChildSketchObject = SketchPointObject | AbstractSketchEdgeObject;

/**
 * 集合源闭合遍历步骤。
 */
export type SketchCollectionTraversalStep = SketchClosureTraversalStep;


/**
 * 集合源绑定状态枚举。
 */
enum SketchCollectionSourceBindingState {
    /** 当前未执行集合源绑定。 */
    Idle = 'idle',
    /** 当前正在执行集合源绑定。 */
    Binding = 'binding',
}


export class SketchCollectionObject extends THREE.Group implements ISketchCollect {
    isSketchCollectionObject = true;

    /** 跨会话稳定持久 ID；`cloneSelf` 继承，复制出新实体的流程必须重新生成。 */
    public persistentId: string = createSketchEntityPersistentId();
    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Collection;
    public readonly isSketchObject: boolean = true;
    /** 线段集合所属参考面。 */
    public readonly referencePlane: ReferencePlane;

    /** 集合创建来源。 */
    public readonly creationSource: SketchCollectionCreationSource;

    // @ts-ignore
    public readonly constraints = new SketchRelationContainer(this);
    public readonly collectSet: Set<CollectSource> = new Set();
    /** 集合几何载荷。 */
    public geometryData: Record<string, any> | null = null;
    /** 当前线段几何创建出的轮廓对象缓存。 */
    private contour: SketchContour | null = null;
    /** 线段几何对象拖动留影代理。 */
    public readonly dragSnapshotProxy: SketchSnapshot = new SketchSnapshot();

    /** 集合源绑定状态。 */
    private sourceBindingState: SketchCollectionSourceBindingState = SketchCollectionSourceBindingState.Idle;

    /** 当前集合拖拽会话中已经实际启动留影的控制点。 */
    private readonly activeDragPreviewPoints = new Set<SketchPointObject>();


    /**
     * 判断当前集合是否能围成封闭几何。
     * 说明：
     * 1. 直接基于当前 `collectSet` 中的对象顺序判定；
     * 2. 仅返回布尔值，不抛错；
     * 3. 供外部统一读取集合闭合状态。
     * @returns 当前集合是否闭合。
     */
    public get isClosedGeometry(): boolean {
        return SketchCollectionObject.canFormClosedGeometry(Array.from(this.collectSet));
    }

    /**
     * 构造草图线段几何对象
     * @param options 构造参数
     */
    public constructor(options: SketchCollectionObjectOptions) {
        super();
        this.referencePlane = options.referencePlane;

        this.creationSource = options.creationSource ?? SketchCollectionCreationSource.Generic;

        this.setSources(options.sources);
    }


    /**
     * 以新列表替换当前集合源对象。
     * @param sources 新源对象列表。
     */
    public setSources(sources: CollectSource[]): void {


        sources.forEach((source) => {
            if (!isSketchCollectionSource(source)) {
                throw new Error('检测到非法集合源对象，无法绑定闭合集合。');
            }
        });


        // 替换集合源前，先移除旧轮廓对象，避免残留失效网格。
        if (this.contour && this.contour.parent === this) {
            this.remove(this.contour);
        }
        this.contour = null;

        this.collectSet.forEach((source) => {
            if (!isSketchCollectionSource(source)) {
                throw new Error('检测到非法集合源对象，无法解绑闭合集合。');
            }
            source.detachClosedCollection(this);
            if (source.parent === this) {
                this.remove(source);
            }
        });
        this.collectSet.clear();


        /**
         * 先建立完整集合拓扑，再回调边对象绑定宿主。
         * 原实现逐条 attach 后再写入 collectSet，在毫米级短边下会让前缀边链被固定容差误判为闭合，
         * 从而提前创建只包含一条边的空 contour。
         */
        sources.forEach((source) => {
            this.collectSet.add(source);
        });
        this.sourceBindingState = SketchCollectionSourceBindingState.Binding;
        try {
            sources.forEach((source) => {
                source.attachClosedCollection(this);
                if (source.parent !== this) {
                    this.add(source);
                }
            });
        } finally {
            this.sourceBindingState = SketchCollectionSourceBindingState.Idle;
        }


    }


    /**
     * 获取当前线段几何的轮廓对象。
     *
     * 设计说明：
     * 1. 上色草图轮廓只是显示对象，退出草图后允许从场景中移除；
     * 2. 拉伸等特征仍需要通过闭合集合按需恢复 profile 轮廓；
     * 3. 因此这里不把“是否有显示填充面”作为轮廓能力判定依据。
     *
     * @returns 轮廓对象；若当前集合不闭合则返回 null。
     */
    public getContour(): SketchContour | null {

        if (this.sourceBindingState === SketchCollectionSourceBindingState.Binding) {
            return null;
        }

        if (this.contour) {
            return this.contour;
        }
        if (!this.canCreateContour()) {
            return null;
        }
        return this.createContour();
    }

    /**
     * 判断当前集合是否可创建轮廓。
     * @returns 当前集合闭合时返回 true。
     */
    public canCreateContour(): boolean {
        return this.isClosedGeometry;
    }

    /**
     * 获取当前集合中的全部线段源快照。
     *
     * 设计说明：
     * 1. 集合对象是线/弧/样条等边对象的稳定宿主，对外应提供受控读取出口；
     * 2. 返回快照而不是内部 Set，避免宿主层直接修改集合源；
     * 3. 该接口服务于属性面板、槽口编辑等需要批量遍历线段源的场景。
     *
     * @returns 当前集合中的线段源列表快照。
     */
    public getLines(): readonly CollectSource[] {
        return Array.from(this.collectSet);
    }


    /**
     * 复制当前集合对象，返回与源对象完全解耦的新实例。
     * 说明：
     * 1. 集合内共享端点会通过复制上下文保持在新集合中继续共享；
     * 2. 新集合不会保留旧集合的轮廓对象引用。
     * @param context 复制上下文。
     * @returns 当前集合对象的独立副本。
     */
    public cloneSelf(context: SketchCloneContext = createSketchCloneContext()): SketchCollectionObject {
        const clonedSources = Array.from(this.collectSet).map((source) => source.cloneSelf(context)) as CollectSource[];
        const clonedCollection = new SketchCollectionObject({
            referencePlane: this.referencePlane,
            sources: clonedSources,

            creationSource: this.creationSource,

        });


        // 复制集合直接子层级中的补充草图对象，确保多边形辅助圆等非 collectSet 对象不会丢失。
        this.collectCloneableDirectChildSketchObjects().forEach((child) => {
            const existingClone = context.objectMap.get(child.uuid) as unknown as THREE.Object3D | undefined;
            const clonedChild = existingClone ?? child.cloneSelf(context);
            if (clonedChild.parent !== clonedCollection) {
                clonedCollection.add(clonedChild);
            }
        });

        // 阵列定义与多边形共享生命周期，历史克隆时必须随集合子树一起复制。
        this.children.filter((child) => (
            Reflect.get(child, 'isSketchCircularPatternObject') === true
        )).forEach((child) => {
            const clonedChild = child.clone(true);
            if (clonedChild.parent !== clonedCollection) {
                clonedCollection.add(clonedChild);
            }
        });


        // 副本表达同一逻辑集合，必须继承持久身份；复制出新实体的流程负责显式重新生成。
        clonedCollection.persistentId = this.persistentId;
        clonedCollection.position.copy(this.position);
        clonedCollection.quaternion.copy(this.quaternion);
        clonedCollection.scale.copy(this.scale);
        clonedCollection.visible = this.visible;
        clonedCollection.renderOrder = this.renderOrder;
        clonedCollection.frustumCulled = this.frustumCulled;
        clonedCollection.layers.mask = this.layers.mask;

        if (this.geometryData) {
            clonedCollection.geometryData = structuredClone(this.geometryData);
        }
        // 历史快照只复制可由当前真实拓扑重建的轮廓；遗留显示对象不能反向证明集合仍然闭合。
        if (this.contour && clonedCollection.isClosedGeometry) {
            clonedCollection.syncContour();
        }

        // 集合本身也可作为多目标关系目标，复制上下文必须能重绑顶层身份。
        context.objectMap.set(this.uuid, clonedCollection);

        return clonedCollection;
    }


    /**
     * 收集当前集合直接子层级中的可复制草图对象。
     * 说明：
     * 1. `collectSet` 源边已经在集合构造阶段复制，这里通过 context 复用其副本；
     * 2. 多边形辅助圆这类直接挂在集合下、但不属于 `collectSet` 的边对象必须在此补齐；
     * 3. 只处理直接子层级，边对象内部端点仍由各自 `cloneSelf()` 负责。
     * @returns 当前集合直接子层级中可复制的草图对象列表。
     */
    private collectCloneableDirectChildSketchObjects(): CloneableDirectChildSketchObject[] {
        return this.children.filter((child): child is CloneableDirectChildSketchObject => (
            isSketchPointObject(child) || isAbstractSketchEdgeObject(child)
        ));
    }


    /**
     * 同步线段几何轮廓网格。
     * 说明：
     * 1. 每次调用都会重建轮廓，避免轮廓与线段状态不一致；
     * 2. 若已存在旧轮廓，会先从分组移除再替换为新轮廓；
     * 3. 轮廓创建失败直接抛错，不做兜底。
     */
    public syncContour(): void {
        if (this.hasConstructionContourSources()) {
            this.clearContourDisplay();
            return;
        }
        this.clearContourDisplay();
        const contour = this.createContour();
        if (!contour) {
            this.contour = null;
            return;
        }
        this.contour = contour;
        this.add(contour);
    }

    /**
     * 清理当前轮廓显示。
     * 说明：
     * 1. 仅移除当前集合对象上挂载的轮廓实例；
     * 2. 同时清理集合源对象上的轮廓引用；
     * 3. 不修改集合源对象本身的几何。
     */
    public clearContourDisplay(): void {
        const contour = this.contour;
        if (contour && contour.parent === this) {
            this.remove(contour);
        }
        if (contour) {
            disposeSketchContour(contour);
        }
        this.contour = null;
        this.collectSet.forEach((source) => {
            source.clearContour();
        });
    }


    /**
     * 刷新当前轮廓几何（保持轮廓对象实例不变）。
     * 说明：
     * 1. 仅当轮廓已创建时执行更新；
     * 2. 用于拖拽过程中实时同步封闭线段几何的上色轮廓；
     * 3. 若当前集合处于圆角/倒角替换端点等瞬时非闭合中间态，则跳过本次刷新，等待最终闭合后再统一刷新。
     */
    public refreshContourGeometry(): void {
        if (!this.contour) {
            return;
        }
        if (this.hasConstructionContourSources()) {
            this.clearContourDisplay();
            return;
        }
        if (!this.isClosedGeometry) {
            return;
        }
        this.contour.syncSource();
    }

    /**
     * 在一次完整交互结束后统一收口轮廓显示状态。
     *
     * 说明：
     * 1. 圆角、倒角和多点替换过程中允许短暂出现非闭合中间态，因此高频刷新阶段不能立即删除轮廓；
     * 2. 拖动结束属于稳定边界，若集合此时仍未闭合，旧填充必须释放，禁止污染历史快照；
     * 3. 集合保持闭合时复用现有轮廓对象同步几何，避免无意义重建 GPU 资源。
     */
    private reconcileContourAfterInteraction(): void {
        if (!this.contour) {
            return;
        }
        if (this.hasConstructionContourSources() || !this.isClosedGeometry) {
            this.clearContourDisplay();
            return;
        }
        this.contour.syncSource();
    }


    /**
     * 基于当前线段集合创建轮廓对象。
     * @returns 新创建的轮廓对象。
     * @throws 当非构造线集合尚未闭合时抛出错误。
     */
    public createContour(): SketchContour | null {
        if (this.hasConstructionContourSources()) {
            return null;
        }
        if (!this.isClosedGeometry) {
            throw new Error('当前集合未闭合，无法创建轮廓。');
        }
        const contour = new SketchContour({
            source: this
        });
        this.contour = contour;
        this.collectSet.forEach((source) => {
            source.contour = contour;
        });
        return contour;
    }

    /**
     * 判断当前集合是否包含构造边。
     * @returns 只要任一组成边是构造线则返回 true。
     */
    private hasConstructionContourSources(): boolean {
        return Array.from(this.collectSet).some((source) => source.isConstruction);
    }


    /**
     * 拖动开始时显示集合完整留影。
     *
     * 设计说明：
     * 1. 集合的移动成员不只包含 `collectSet` 外框边，多边形辅助构造圆等直接子边也会同步移动；
     * 2. 控制点从全部移动边的关联点中统一收集，并按 UUID 去重；
     * 3. 点对象自行区分视觉快照与无视觉生命周期，隐藏点继承隐藏状态，未挂载点只通知关联边；
     * 4. 实际启动过的点会被记录，保证异常与正常结束路径都能对称清理。
     */
    public beginDragPreview(): void {
        const visitedPointIds = new Set<string>();
        this.activeDragPreviewPoints.clear();

        try {
            this.collectDragPreviewEdges().forEach((edge) => {
                edge.getRelatedSketchPointsSnapshot().forEach((point) => {
                    if (visitedPointIds.has(point.uuid)) {
                        return;
                    }
                    visitedPointIds.add(point.uuid);

                    point.beginDragPreview();
                    this.activeDragPreviewPoints.add(point);
                });
            });

            this.dragSnapshotProxy.show(this);
        } catch (error) {
            // 任一成员留影创建失败时立即回滚已启动的点，禁止留下半套拖拽视觉状态。
            this.activeDragPreviewPoints.forEach((point) => point.endDragPreview());
            this.activeDragPreviewPoints.clear();
            throw error;
        }
    }

    /**
     * 拖动结束时隐藏集合完整留影。
     */
    public endDragPreview(): void {
        try {
            this.dragSnapshotProxy.hide();
        } finally {
            try {
                // 只结束本次实际启动过的点，避免成员集合变化后误减其它拖拽会话的嵌套深度。
                this.activeDragPreviewPoints.forEach((point) => point.endDragPreview());
            } finally {
                this.activeDragPreviewPoints.clear();
                // 点级拖拽生命周期全部结束后，集合拓扑已经稳定，可安全决定保留或释放轮廓。
                this.reconcileContourAfterInteraction();
            }
        }
    }

    /**
     * 收集会随当前集合一起移动的全部边对象。
     *
     * @returns 已按 UUID 去重的集合源边与直接子边快照。
     */
    private collectDragPreviewEdges(): readonly AbstractSketchEdgeObject[] {
        const edgeMap = new Map<string, AbstractSketchEdgeObject>();

        this.collectSet.forEach((source) => {
            edgeMap.set(source.uuid, source);
        });
        this.children.forEach((child) => {
            // 直接子边用于承载多边形辅助圆等不参与闭合判定、但参与整体移动的派生草图实体。
            if (isAbstractSketchEdgeObject(child)) {
                edgeMap.set(child.uuid, child);
            }
        });

        return Array.from(edgeMap.values());
    }


    /**
     * 判断当前集合对象是否允许拖动。
     * 说明：
     * 1. 先检查集合对象自身固定约束；
     * 2. 再检查集合中的每个源对象是否允许拖动；
     * 3. 任一集合源对象不可拖动时，整个集合视为不可拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public canDrag(): boolean {
        return !this.constraints.hasType(SketchRelationKind.Fix)
            && Array.from(this.collectSet).every((source) => source.canDrag());
    }



    /**
     * 基于种子边与候选边解析一条首尾连续闭合的有向遍历链。
     * 说明：
     * 1. `seedSources` 必须已经是按绘制顺序排列的开链或闭链；
     * 2. `candidateSources` 允许混合传入直线、圆弧、部分椭圆、抛物线、样条与样式样条；
     * 3. 闭合规则允许边对象在遍历过程中按正向或反向参与，但不会修改边对象自身定义；
     * 4. 返回结果为可直接用于闭合判定与轮廓采样的遍历步骤列表；解析失败返回 null。
     * @param seedSources 种子边列表。
     * @param candidateSources 候选边列表。
     * @returns 闭合遍历步骤列表；无法闭合时返回 null。
     */
    public static resolveClosedGeometryTraversal(
        seedSources: readonly CollectSource[],
        candidateSources: readonly CollectSource[] = [],
    ): SketchCollectionTraversalStep[] | null {
        return SketchClosureResolver.resolveClosedGeometryTraversal(seedSources, candidateSources);
    }

    /**
     * 基于种子边与候选边解析一条首尾连续闭合的有序边链。
     * 说明：
     * 1. 对外仍保留“仅返回源对象列表”的稳定出口，避免影响既有调用方；
     * 2. 具体遍历方向由 `resolveClosedGeometryTraversal(...)` 负责；
     * 3. 返回值中的源对象顺序与闭合遍历顺序一致。
     * @param seedSources 种子边列表。
     * @param candidateSources 候选边列表。
     * @returns 闭合有序边链；无法闭合时返回 null。
     */
    public static resolveClosedGeometrySources(
        seedSources: readonly CollectSource[],
        candidateSources: readonly CollectSource[] = [],
    ): CollectSource[] | null {
        return SketchClosureResolver.resolveClosedGeometrySources(seedSources, candidateSources);
    }


    /**
     * 判断集合源对象是否能围成封闭几何。
     * @param sources 待判定的集合源对象列表。
     * @returns 若首尾连续闭合则返回 true，否则返回 false。
     */
    public static canFormClosedGeometry(sources: CollectSource[]): boolean {
        return SketchClosureResolver.canFormClosedGeometry(sources);
    }

    /**
     * 获取遍历步骤在当前方向下的起止端点。
     * @param step 遍历步骤。
     * @returns 当前方向对应的起止端点。
     */
    public static getTraversalStepEndpoints(
        step: SketchCollectionTraversalStep,
    ): {startPoint: SketchPointObject; endPoint: SketchPointObject} {
        return SketchClosureResolver.getTraversalStepEndpoints(step);
    }

    /**
     * 获取集合源对象的起止端点。
     * 说明：
     * 1. 通过 `getEdgeStartPoint` 与 `getEdgeEndPoint` 运行时协议读取；
     * 2. 集合对象不扩展边对象公共接口，避免污染稳定模块；
     * 3. 若协议缺失直接抛错，不做兜底处理。
     * @param source 集合源对象。
     * @returns 起点与终点。
     */
    public static getSourceEndpoints(
        source: CollectSource,
    ): {startPoint: SketchPointObject; endPoint: SketchPointObject} {
        return SketchClosureResolver.getSourceEndpoints(source);
    }
}
